import { useCallback, useEffect, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase-config.js";
import { applyThemeColor } from "../utils/themeColor";

const DARK_MODE_EVENT = "harmonauria:dark-mode-change";
const THEME_KEY = "harmonauria:theme";

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === "dark" || stored === "light" ? stored : null;
  } catch {
    return null;
  }
}

/* Le thème doit pouvoir être réappliqué depuis n'importe où : les pages
   publiques forcent le mode clair et doivent rendre la main à la préférence. */
export function applyStoredTheme() {
  const theme = readStoredTheme() || "light";
  document.documentElement.setAttribute("data-theme", theme);
  applyThemeColor(theme === "dark");
}

export function useDarkMode() {
  const [isDarkMode, setIsDarkMode] = useState(() => (readStoredTheme() || document.documentElement.getAttribute("data-theme")) === "dark");
  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);
  const didMountRef = useRef(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (currentUser) => {
      setUid(currentUser?.uid || null);
    });
  }, []);

useEffect(() => {
  if (!uid) return undefined;

  return onSnapshot(doc(db, "users", uid), (snapshot) => {
    const theme = snapshot.exists() ? snapshot.data()?.uiPreferences?.theme : null;

    if (theme === "dark") {
      setIsDarkMode(true);
    } else if (theme === "light") {
      setIsDarkMode(false);
    }
  }, (error) => {
    console.warn("Impossible de charger le thème du compte.", error);
  });
}, [uid]);

  useEffect(() => {
    const theme = isDarkMode ? "dark" : "light";

    document.documentElement.setAttribute("data-theme", theme);
    applyThemeColor(isDarkMode);

    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* La préférence reste synchronisée dans Firestore. */
    }

    if (didMountRef.current) {
      window.dispatchEvent(new CustomEvent(DARK_MODE_EVENT, {
        detail: { isDarkMode },
      }));
    } else {
      didMountRef.current = true;
    }
  }, [isDarkMode]);

  useEffect(() => {
    const handleSameTabChange = (event) => {
      if (typeof event.detail?.isDarkMode !== "boolean") return;
      setIsDarkMode(event.detail.isDarkMode);
    };

    window.addEventListener(DARK_MODE_EVENT, handleSameTabChange);
    return () => window.removeEventListener(DARK_MODE_EVENT, handleSameTabChange);
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem("harmonauria_dark");
    } catch {
      /* noop */
    }
  }, []);

  const toggleDarkMode = useCallback(() => {
    const nextValue = !isDarkMode;

    setIsDarkMode(nextValue);

    if (!uid) return;

    void updateDoc(doc(db, "users", uid), {
      "uiPreferences.theme": nextValue ? "dark" : "light",
    }).catch((error) => {
      console.warn("Impossible d’enregistrer le thème du compte.", error);
    });
  }, [isDarkMode, uid]);

  return { isDarkMode, toggleDarkMode };
}